import type { AxiosRequestConfig } from "axios";
import type { IRepositoryOptions } from "@/types/common";
import type { IUser } from "@/types/users";
import type { IContractor } from "@/types/users-contractors";
import type { ISuperAdmin } from "@/types/users-super-admins";
import { FileUtils } from "../../utils/FileUtils";
import { BaseRepository } from "../BaseRepository";
import type { UsersService } from "./service";
import type {
  IClaimInviteRequest,
  IDeleteInvitationRequest,
  IFlagWrongCompanyRequest,
  IGetUserByIdRequest,
  IGetUsersByRoleRequest,
  IInviteDetailsRequest,
  IInviteDetailsResponse,
  IInviteUserRequest,
  IResendInvitationRequest,
  IUpdateOnboardingStatusRequest,
  IUpdatePasswordRequest,
  IUpdateSuperAdminDetailsRequest,
  IUpdateSuperAdminPictureInput,
  IUpdateUserDetailsRequest,
  IUpdateUserPictureInput,
  IUpdateUserPictureRequest,
} from "./types";

export class UsersRepository extends BaseRepository {
  constructor(
    private readonly service: UsersService,
    options?: IRepositoryOptions,
  ) {
    super(options);
  }

  async getCurrentUser(c?: AxiosRequestConfig): Promise<IUser> {
    const response = await this.handleRequest(this.service.getCurrentUser, c);
    return response.data;
  }

  async inviteUser(
    p: IInviteUserRequest,
    c?: AxiosRequestConfig,
  ): Promise<IContractor> {
    const payload: IInviteUserRequest = {
      ...p,
      body: {
        ...p.body,
        fullName: p.body.fullName.trim(),
        email: p.body.email.trim(),
      },
    };
    const response = await this.handleRequest(
      this.service.inviteUser,
      payload,
      c,
    );
    return response.data;
  }

  async resendInvitation(
    p: IResendInvitationRequest,
    c?: AxiosRequestConfig,
  ): Promise<void> {
    const payload: IResendInvitationRequest = {
      fullName: p.fullName.trim(),
      email: p.email.trim(),
    };
    await this.handleRequest(this.service.resendInvitation, payload, c);
  }

  async deleteInvitation(
    p: IDeleteInvitationRequest,
    c?: AxiosRequestConfig,
  ): Promise<void> {
    await this.handleRequest(this.service.deleteInvitation, p, c);
  }

  async getUsersByRole(
    p: IGetUsersByRoleRequest,
    c?: AxiosRequestConfig,
  ): Promise<IUser[]> {
    const response = await this.handleRequest(
      this.service.getUsersByRole,
      p,
      c,
    );
    return response.data;
  }

  async getInviteDetailsByToken(
    p: IInviteDetailsRequest,
    c?: AxiosRequestConfig,
  ): Promise<IInviteDetailsResponse> {
    const response = await this.handleRequest(
      this.service.getInviteDetailsByToken,
      p,
      c,
    );
    return response.data;
  }

  async claimInvite(
    p: IClaimInviteRequest,
    c?: AxiosRequestConfig,
  ): Promise<void> {
    const payload = { ...p };
    payload.id = +p.id;
    delete payload.password_repeat;
    await this.handleRequest(this.service.claimInvite, payload, c);
  }

  async updateUserDetails(
    p: IUpdateUserDetailsRequest,
    c?: AxiosRequestConfig,
  ): Promise<IUser> {
    const response = await this.handleRequest(
      this.service.updateUserDetails,
      p,
      c,
    );
    return response.data;
  }

  async updateOnboardingStatus(
    p: IUpdateOnboardingStatusRequest,
    c?: AxiosRequestConfig,
  ): Promise<IUser> {
    const response = await this.handleRequest(
      this.service.updateOnboardingStatus,
      p,
      c,
    );
    return response.data;
  }

  async updatePassword(
    p: IUpdatePasswordRequest,
    c?: AxiosRequestConfig,
  ): Promise<void> {
    const response = await this.handleRequest(
      this.service.updatePassword,
      p,
      c,
    );
    return response.data;
  }

  async updateProfilePicture(
    p: IUpdateUserPictureInput,
    c?: AxiosRequestConfig,
  ): Promise<IUser> {
    const formData = FileUtils.convertFileToFormData("picture", p.picture);

    const payload: IUpdateUserPictureRequest = {
      userId: p.userId,
      picture: formData,
    };

    const response = await this.handleRequest(
      this.service.updateProfilePicture,
      payload,
      c,
    );
    return response.data;
  }

  async flagWrongCompany(
    p: IFlagWrongCompanyRequest,
    c?: AxiosRequestConfig,
  ): Promise<void> {
    const response = await this.handleRequest(
      this.service.flagWrongCompany,
      p,
      c,
    );
    return response.data;
  }

  async getUserDetails(
    p: IGetUserByIdRequest,
    c?: AxiosRequestConfig,
  ): Promise<IUser> {
    const response = await this.handleRequest(
      this.service.getUserDetails,
      p,
      c,
    );
    return response.data;
  }

  async getRequiresReconsent(c?: AxiosRequestConfig): Promise<boolean> {
    const response = await this.handleRequest(
      this.service.getRequiresReconsent,
      c,
    );
    return response.data;
  }

  async postPrivacyConsent(c?: AxiosRequestConfig): Promise<void> {
    await this.handleRequest(this.service.postPrivacyConsent, c);
  }

  async updateSuperAdminDetails(
    p: IUpdateSuperAdminDetailsRequest,
    c?: AxiosRequestConfig,
  ): Promise<ISuperAdmin> {
    const response = await this.handleRequest(
      this.service.updateSuperAdminDetails,
      p,
      c,
    );
    return response.data;
  }

  async updateSuperAdminProfilePicture(
    p: IUpdateSuperAdminPictureInput,
    c?: AxiosRequestConfig,
  ): Promise<ISuperAdmin> {
    const formData = FileUtils.convertFileToFormData("picture", p.picture);

    const payload: IUpdateUserPictureRequest = {
      userId: p.userId,
      picture: formData,
    };

    const response = await this.handleRequest(
      this.service.updateSuperAdminProfilePicture,
      payload,
      c,
    );
    return response.data;
  }
}
